import { SURVEY_DISPLAY_ACRONYMS } from '@/surveys/constants/surveys.constants';
import * as api from '@/vuex/modules/surveys/api/surveys.api.js';
import { runAggregationOnce } from '@utils/aggregation';
import SurveyItemsAllAggregation from '@/surveys/aggregations/survey-items/survey-items-all.aggregation';

const state = {
    surveys: {},
    surveyItems: [],
    promises: {},
};

const getters = {
    list: (state) => state.surveys,
    listItems: (state) => state.surveyItems,
    surveyById: (state) => (id) => state.surveys[id],
};

const mutations = {
    setItems (state, { surveyItems }) {
        state.surveyItems = surveyItems;
    },
    setMap (state, { surveys }) {
        state.surveys = surveys;
    },
    setMapAtKey (state, { key, value }) {
        state.surveys[key] = value;
    },
    updateMapAtKey (state, { key, updates }) {
        state.surveys[key] = { ...state.surveys[key], ...updates };
    },
    deleteByKey (state, key) {
        delete state.surveys[key];
    },
    setPromise (state, { key, promise }) {
        state.promises[key] = promise;
    },
};

const actions = {
    async loadAll ({ state, commit }) {
        if (state.promises.loadAll) {
            return state.promises.loadAll;
        }

        const loadAllPromise = api
            .fetchSurveys()
            .then((surveys) => {
                commit('setMap', { surveys });

                return surveys;
            })
            .finally(() => {
                commit('setPromise', { key: 'loadAll', promise: null });
            });

        commit('setPromise', { key: 'loadAll', promise: loadAllPromise });

        return loadAllPromise;
    },
    async loadOne ({ state, commit }, { id }) {
        if (state.promises[`loadOne-${id}`]) {
            return state.promises[`loadOne-${id}`];
        }

        const loadOnePromise = api
            .fetchSurvey(id)
            .then((survey) => {
                commit('setMapAtKey', { key: id, value: survey });

                return survey;
            })
            .finally(() => {
                commit('setPromise', { key: `loadOne-${id}`, promise: null });
            });

        commit('setPromise', { key: `loadOne-${id}`, promise: loadOnePromise });

        return loadOnePromise;
    },
    async loadAllItems ({ state, commit }) {
        if (state.promises.loadAllItems) {
            return state.promises.loadAllItems;
        }

        const loadItemsPromise = runAggregationOnce(SurveyItemsAllAggregation, {})
            .then((surveyItems) => {
                commit('setItems', { surveyItems });
            })
            .catch((error) => {
                throw error;
            });
        commit('setPromise', { key: 'loadAllItems', loadItemsPromise });

        return loadItemsPromise;
    },
    async createFromTemplate ({ commit }, { type, productName, appId, productAreaIds, deliveryMethods, guideLayout, hideThankYou, snoozeEnabled }) {
        const payload = {
            surveyType: type,
            surveyData: {
                productName,
            },
            name: `${SURVEY_DISPLAY_ACRONYMS[type]} Survey for ${productName}`,
            appIds: [appId],
            productAreaIds,
            delivery: deliveryMethods,
            hideThankYou,
            singleStep: guideLayout && guideLayout !== 'MULTI_STEP',
            snoozeEnabled,
        };

        const res = await api.createSurveyFromTemplate({ payload });

        const survey = {
            ...res.survey,
            items: res.surveyItems,
        };

        commit('setMapAtKey', { key: survey.id, value: survey });

        return survey;
    },
    async updateStatus ({ commit }, { id, status }) {
        const survey = await api.updateSurveyStatus({ id, status });

        commit('setMapAtKey', { key: survey.id, value: survey });

        return survey;
    },
    async updateSurveyName ({ commit, getters }, { id, name }) {
        const rollbackSurveyState = getters.surveyById(id);
        commit('updateMapAtKey', { key: id, updates: { name } });

        try {
            const updatedSurvey = await api.updateSurveyDetails({
                id,
                payload: {
                    name,
                },
            });

            commit('setMapAtKey', { key: id, value: updatedSurvey });

            return updatedSurvey;
        } catch (error) {
            commit('setMapAtKey', { key: id, value: rollbackSurveyState });
            throw error;
        }
    },
    async updateSurveyProductAreaId ({ commit }, { id, productAreaIds }) {
        const survey = await api.updateSurveyDetails({
            id,
            payload: {
                productAreaIds,
            },
        });

        commit('setMapAtKey', { key: survey.id, value: survey });

        return survey;
    },
    async updateSurveySnoozeEnabled ({ commit }, { id, snoozeEnabled }) {
        const survey = await api.updateSurveyDetails({
            id,
            payload: {
                snoozeEnabled,
            },
        });

        commit('updateMapAtKey', { key: survey.id, updates: survey });

        return survey;
    },
    async updateDelivery ({ commit }, { id, audienceConfig, deliveryId }) {
        const survey = await api.updateSurveyDelivery({
            id,
            audienceConfig,
            deliveryId,
        });

        commit('setMapAtKey', { key: survey.id, value: survey });

        return survey;
    },
    async updateDeliveryItems ({ commit }, { id, deliveryId, deliveryConfig }) {
        const survey = await api.updateSurveyDeliveryItems({
            id,
            deliveryId,
            deliveryConfig,
        });

        commit('setMapAtKey', { key: survey.id, value: survey });

        return survey;
    },
    async updateSurveyRecurrence ({ commit }, params) {
        const survey = await api.updateSurveyRecurrence(params);

        commit('setMapAtKey', { key: survey.id, value: survey });

        return survey;
    },
    async delete ({ commit }, { id }) {
        await api.deleteSurvey(id);

        commit('deleteByKey', id);
    },

    async cloneSurvey ({ commit }, { surveyId, targetAppId }) {
        const res = await api.cloneSurvey({ surveyId, targetAppId });

        const survey = {
            ...res.survey,
            items: res.surveyItems,
        };

        commit('setMapAtKey', { key: survey.id, value: survey });

        return survey;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
